import React, {
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  createRef,
} from "react";
import { v4 as uuid } from "uuid";
import classNames from "classnames";
import { isNull } from "lodash";
import {
  Handle,
  Position,
  useUpdateNodeInternals,
  NodeResizer,
} from "reactflow";
import { INDEX, HANDLE_TYPES, EMPTY_ARRAY, ONE } from "../../../constants";
import { CUSTOM } from "../../../pages/DataModels/datamodels.constants";
import { circleIcon, dashIcon, equalIcon } from "../../../assets/icons";

const { FIRST, THIRD } = INDEX;
const TARGET = "target";
const SOURCE = "source";
const { Top, Bottom } = Position;
const { OBJECT, LIST, VALUE } = HANDLE_TYPES;
const GROUP_COLOR = [
  "border-green-400",
  "border-yellow-400",
  "border-indigo-300",
  "border-violet-400",
  "border-lime-600",
  "border-stone-400",
  "border-sky-600",
  "border-blue-300",
  "border-gray-600",
  "border-gray-600",
  "border-lime-600",
];
const INTERNAL_OUTPUT = "internalOutput";
const INTERNAL_INPUT = "internalInput";

const ResizableNode = ({ data, selected }) => {
  const {
    outputs,
    inputs,
    name,
    group,
    id,
    isValidated,
    nodeDescription,
    groupId,
    isParent,
    isSubParent,
    highlight,
  } = data;

  const updateNodeInternals = useUpdateNodeInternals();
  const [nodeWidth, setNodeWidth] = useState();
  const [nodeHeight, setNodeHeight] = useState();
  const nodeRef = createRef();

  useLayoutEffect(() => {
    if (nodeRef.current) {
      setNodeWidth(nodeRef.current.clientWidth);
      setNodeHeight(nodeRef.current.clientHeight);
    }
  }, [nodeRef, data]);

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, updateNodeInternals, inputs, outputs]);

  const getPosition = useCallback(
    (handleCount, idx) =>
      `${(nodeWidth / handleCount) * idx + nodeWidth / handleCount / THIRD}px`,
    [nodeWidth]
  );

  const getParentNodePosition = useCallback(
    (handles, handleCount, index) => {
      const internalHandleIndex = handles.reduce(
        (handleIndex, { key }, index) => {
          if (key === INTERNAL_INPUT || key === INTERNAL_OUTPUT) {
            return index;
          }
          return handleIndex;
        },
        null
      );
      if (!isNull(internalHandleIndex)) {
        const updatedIndex = internalHandleIndex < index ? index - ONE : index;
        return getPosition(handleCount - ONE, updatedIndex);
      } else return getPosition(handleCount, index);
    },
    [getPosition]
  );

  const getHandleBackground = useCallback((type) => {
    switch (type) {
      case OBJECT:
        return `url('${dashIcon}')`;
      case LIST:
        return `url('${equalIcon}')`;
      case VALUE:
        return `url('${circleIcon}')`;
      default:
        return;
    }
  }, []);

  const renderParentInternalHandle = useCallback(
    (handleType, key) => {
      const isTargetHandle = handleType === TARGET;
      const position = isTargetHandle ? Top : Bottom;
      const handleStyling = isTargetHandle
        ? { top: `${nodeHeight - 12}px` }
        : EMPTY_ARRAY;
      return (
        <div onClick={(e) => e.stopPropagation()} key={key}>
          <Handle
            key={`tava_${key}`}
            type={handleType}
            isConnectable={true}
            position={position}
            id={`tava_${key}`}
            className={classNames(
              "!h-0.5 !w-full !bg-transparent !border-0  !border-blue-500 !rounded-md mt-1",
              { "!border-cyan-500": isSubParent },
              { "!border-t-2": isTargetHandle },
              { "!border-b-2 !top-3.5": !isTargetHandle }
            )}
            style={handleStyling}
          />
        </div>
      );
    },
    [isSubParent, nodeHeight]
  );

  const renderHandles = useCallback(
    (handles, handleType) => {
      const handleCount = handles.length;

      return handles.map(({ name, key, type }, idx) => {
        if (key === INTERNAL_OUTPUT || key === INTERNAL_INPUT)
          return renderParentInternalHandle(handleType, key, idx);
        else {
          const handlePosition = handleType === TARGET ? Top : Bottom;
          const leftAlignment = isParent
            ? getParentNodePosition(handles, handleCount, idx)
            : getPosition(handleCount, idx);
          return name === CUSTOM ? (
            <div onClick={(e) => e.stopPropagation()} key={key}>
              <Handle
                key={`tava_${key}`}
                type={handleType}
                isConnectable={true}
                position={handlePosition}
                id={`tava_${key}_${name}`}
                className="!h-3 !w-full !bg-transparent !border-0 !border-t-2 !border-blue-500 !rounded-md mt-1"
              />
            </div>
          ) : (
            <div onClick={(e) => e.stopPropagation()} key={key}>
              <Handle
                key={`tava_${key}`}
                type={handleType}
                position={handlePosition}
                id={`tava_${key}`}
                className="flex justify-evenly items-center !h-2 !w-2 !bg-white !border-gray-600 !border-1"
                style={{
                  left: leftAlignment,
                  backgroundImage: getHandleBackground(type),
                  backgroundSize: "cover",
                }}
              >
                <h6
                  className={classNames(
                    "text-[0.3rem] px-1 rounded-xl bg-gray-200 absolute border-opacity-100",
                    {
                      "mt-5": handleType === SOURCE,
                      "mr-5": idx === FIRST,
                      "ml-5": idx !== FIRST,
                      "mb-5": handleType === TARGET,
                    }
                  )}
                >
                  {name}
                </h6>
              </Handle>
            </div>
          );
        }
      });
    },
    [
      getPosition,
      getHandleBackground,
      renderParentInternalHandle,
      isParent,
      getParentNodePosition,
    ]
  );

  const customHandle = [
    {
      name: CUSTOM,
      key: uuid(),
    },
  ];

  return isParent ? (
    <div
      className={classNames(
        "min-h-[150px]  h-full border border-slate-300 rounded-md",
        { "shadow-md bg-slate-300/50 min-w-[300px]": !isSubParent },
        { "shadow-lg bg-slate-200/70 min-w-[200px]": isSubParent },
        {
          "!border-indigo-700 !shadow-[0px_0px_13px_2px_rgba(0,0,0,0.2)]":
            highlight,
        }
      )}
      ref={nodeRef}
    >
    {data.multiInput && renderHandles(customHandle, TARGET)}
      <div className="p-2 flex items-center">
        <div className="h-full text-[6px]">
          {name}
          {isSubParent && (
            <span className="ml-0.5 italic text-[5px] ">(Sub Parent)</span>
          )}
        </div>
        <div
          className={classNames("h-1 w-1 rounded-full ml-1", {
            "bg-red-600": !isValidated,
            "bg-green-600": isValidated,
          })}
        ></div>
      </div>
      <NodeResizer
        color="#94a3b8"
        isVisible={selected}
        minWidth={isSubParent ? 200 : 300}
        minHeight={150}
      />
      {outputs && renderHandles(outputs, SOURCE)}
      {inputs && renderHandles(inputs, TARGET)}
    </div>
  ) : (
    <div
      className={classNames(
        "bg-white text-[6px] h-full border border-current text-ellipsis overflow-hidden break-words whitespace-pre-wrap rounded-md shadow-md"
      )}
    >
      <div className="px-4 py-2 rounded-md bg-slate-200 min-h-12" ref={nodeRef}>
        {data.multiInput && renderHandles(customHandle, TARGET)}
        <div className="flex text-[6px] justify-center text-center items-center">
          {name}
        </div>
        <div
          className={classNames("flex justify-center italic text-[5px]", {
            hidden: !data.parentNode,
          })}
        >
          (child)
        </div>
        <div className="flex justify-center items-center ">
          <div className={`rounded-md border-2 ${GROUP_COLOR[groupId]} px-1`}>
            {group}
          </div>
        </div>
        <NodeResizer
          color="#94a3b8"
          isVisible={selected}
          minWidth={100}
          minHeight={50}
        />
        {outputs && renderHandles(outputs, SOURCE)}
        {inputs && renderHandles(inputs, TARGET)}
        <div
          className={classNames(
            "h-1 w-1 rounded-full relative flex justify-center items-center m-auto",
            {
              "bg-red-600": !isValidated,
              "bg-green-600": isValidated,
            }
          )}
        ></div>
      </div>
      <div className="px-2 py-1">{nodeDescription}</div>
    </div>
  );
};

export default ResizableNode;
